import { forwardRef, ReactNode } from 'react';

import styled from '@emotion/styled';
import MuiButton, {
  buttonClasses,
  ButtonProps as MuiButtonProps,
} from '@mui/material/Button';
import Color from 'color';

import {
  deleteFromProps,
  transientOptions,
} from '@web/ui/theme/transientOptions';
import { ThemeColorKey } from '@web/ui/theme/type';
import { getBorder } from '@web/ui/theme/utils';

const getBackgroundColor = (
  variant: Exclude<TButtonVariant, 'secondary'>,
): ThemeColorKey => {
  switch (variant) {
    case 'success':
      return 'green';
    case 'warning':
      return 'redFixed';
    case 'primary':
    default:
      return 'yellow';
  }
};

const StyledButton = styled(MuiButton, transientOptions)<{
  $color: ThemeColorKey;
  $borderColor: ThemeColorKey;
}>`
  height: ${({ theme }) => theme.size['2500']};
  padding: 0;

  &.${buttonClasses.root} {
    font: ${({ theme }) => theme.typography.body.regular};
    font-size: ${({ theme }) => theme.typescale.size['1']};
    border-radius: ${({ theme }) => theme.shape.corner['0500']};
    min-width: auto;
  }

  &.${buttonClasses.root}:not(.${buttonClasses.disabled}) {
    color: ${({ theme, $color }) => theme.color[$color]};
    background: ${({ theme }) => theme.color.onYellow};
  }

  @media (hover: hover) {
    :hover:not(:active) > div {
      transform: translate(-4px, -4px);
    }

    :active > div {
      box-shadow: 1px 1px 0px 1px rgba(0, 0, 0, 0.6) inset;

      :after {
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        content: ' ';
        background: rgba(0, 0, 0, 0.1);
      }
    }
  }

  > div {
    ${({ theme, $borderColor, disabled }) =>
      disabled
        ? `
        border-radius: ${theme.shape.corner['0500']};
        color: ${Color(theme.color.inverseSurface).fade(1 - theme.opacity.solid)};
        background: ${Color(theme.color.onSurface).fade(1 - theme.opacity.dim)};
      `
        : getBorder('1', $borderColor, '0500')};
  }
`;

const Moving = styled.div<{
  background: ThemeColorKey;
  disabled?: boolean;
}>`
  box-sizing: border-box;
  height: 100%;
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: ${({ theme, background }) => theme.color[background]};
  padding-inline: ${({ theme }) => theme.spacing('1500')};
  transform: translate(0, 0);
  transition: all 0.1s ease-in-out;
`;

export type TButtonVariant = 'primary' | 'secondary' | 'success' | 'warning';

interface IBaseButtonProps extends Omit<MuiButtonProps, 'variant' | 'color'> {
  label: ReactNode;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

interface IDefaultButtonProps extends IBaseButtonProps {
  variant?: Exclude<TButtonVariant, 'secondary'>;
}

interface ISecondaryButtonProps extends IBaseButtonProps {
  variant: 'secondary';
  color: ThemeColorKey;
  background: ThemeColorKey;
  borderColor?: ThemeColorKey;
}

export type IButtonProps = IDefaultButtonProps | ISecondaryButtonProps;

export const Button = forwardRef<HTMLButtonElement, IButtonProps>(
  (
    {
      label,
      disabled,
      leadingIcon,
      trailingIcon,
      onClick,
      className,
      ...props
    },
    ref,
  ) => {
    const background =
      props.variant === 'secondary'
        ? props.background
        : getBackgroundColor(props.variant || 'primary');

    const color = props.variant === 'secondary' ? props.color : 'onYellow';

    const border =
      props.variant === 'secondary' ? props.borderColor || color : 'onYellow';

    deleteFromProps(props, 'borderColor');

    return (
      <StyledButton
        ref={ref}
        onClick={onClick}
        disabled={disabled}
        className={className}
        disableRipple
        $color={color}
        $borderColor={border}
        {...props}
        color={undefined}
        variant={undefined}
      >
        <Moving
          background={background}
          className={className}
          disabled={disabled}
        >
          {leadingIcon}
          {label}
          {trailingIcon}
        </Moving>
      </StyledButton>
    );
  },
);
